import { min } from "d3";
import { textactive } from "@/_core/_helpers/_parametric_tools";

export class Bar {
  constructor(svg, svgAux = null) {
    this.svg = svg;
    this.svgAux = svgAux ? svgAux : svg;
  }

  // VERTICAL BARS

  bars({
    data,
    customName,
    yField = "value",
    fieldColor,
    fieldBackground = null,
    lineWidth = 1,
    idField = "id",
    candleWidth,
    toZero = true,
    date = "date",
    height = 2,
    yLowerField = 0,
    multipleHeight = false,
    xIsDate = true,
    selectAllElements = "rect",
    offset = 0,
    svgX1 = false,
    keys = [],
  }) {
    this.generalBars({
      data,
      customName,
      yField,
      fieldColor,
      fieldBackground,
      lineWidth,
      idField,
      candleWidth,
      toZero,
      xField: date,
      height,
      yLowerField,
      multipleHeight,
      xIsDate,
      offset,
      selectAllElements,
      svgX1,
      keys,
    });
    return this.svg;
  }

  // VERTICAL BARS GROUP

  barsGroup({
    data,
    customName,
    yField = "value",
    fieldColor,
    fieldBackground = null,
    lineWidth = 1,
    idField = "id",
    candleWidth,
    toZero = true,
    date = "date",
    height = 2,
    yLowerField = 0,
    multipleHeight = false,
    xIsDate = true,
    offset = 0,
    selectAllElements = "rect",
    isMultipleBars = false,
  }) {
    this.svg[customName] = this.createBar(
      "bars" + customName,
      data,
      true,
      isMultipleBars,
    );

    this.generalBars({
      data,
      customName,
      yField,
      fieldColor,
      fieldBackground,
      lineWidth,
      idField,
      candleWidth,
      toZero,
      xField: date,
      height,
      yLowerField,
      multipleHeight,
      xIsDate,
      offset,
      selectAllElements,
      containerElement: false,
    });
    return this.svg;
  }

  // HORIZONTAL BARS

  hBars({
    data,
    barWidth = 0.7,
    name,
    color = textactive,
    fillColor = textactive,
    fillColorAlpha = 0.5,
    xField = "x",
    yField = "name",
    show = true,
    multipleColor = false,
    toZero = true,
    widthAux = 2,
    multipleWidth = false,
    strokeWidth = 1,
    addId = false,
    idField = "id",
  }) {
    // ======= Calculate Width =======
    let [, candleWidth, delta] = this.calculateWidths(barWidth);

    // ======= Horizontal Bar =======
    this.svg.hBar = this.createBar("hbars" + name, data, show);
    const _this = this;
    this.svg.hBar
      .selectAll("rect")
      .data((d) => d)
      .enter()
      .append("rect")
      .filter(function(d, i) {
        return (
          (toZero === true
            ? Math.abs(_this.svg.x(0) - _this.svg.x(d[xField]))
            : multipleWidth === true
            ? Math.abs(_this.svg.x(0) - _this.svg.x(widthAux[i]))
            : widthAux) > 0
        );
      })
      .style("fill", function(d, i) {
        return multipleColor ? fillColor[i] : fillColor;
      })
      .style("fill-opacity", fillColorAlpha)
      .style("stroke", function(d, i) {
        return multipleColor ? color[i] : color;
      })
      .style("stroke-width", strokeWidth)
      .attr("x", function(d) {
        return toZero === true
          ? Math.min(_this.svg.x(0), _this.svg.x(d[xField]))
          : _this.svg.x(d[xField]);
      })
      .attr("y", function(d) {
        return _this.svg.y(d[yField]) + delta;
      })
      .attr("class", function(d) {
        return "hbar" + name + "" + (addId ? d[idField] : d.currency);
      })
      .attr("width", function(d, i) {
        return toZero === true
          ? Math.abs(_this.svg.x(0) - _this.svg.x(d[xField]))
          : multipleWidth === true
          ? Math.abs(_this.svg.x(0) - _this.svg.x(widthAux[i]))
          : widthAux;
      })
      .attr("height", candleWidth)
      .style("shape-rendering", "crispEdges");

    return this.svg;
  }

  // TWO STACKED BARS

  twoStackedBars({
    data,
    barWidth = 0.7,
    firstStackedBarInfo = {
      name: "",
      color: textactive,
      fillColor: textactive,
    },
    secondStackedBarInfo = {
      name: "",
      color: textactive,
      fillColor: textactive,
    },
    xField = "date",
    yField1 = "balance",
    yField2 = "cash_flow",
    show = true,
    strokeWidth = 1,
    offset = 0,
    idField = "id",
    addId = false,
  }) {
    // ======= Calculate Width =======
    let [, candleWidth, delta, offsets] = this.calculateWidths(
      barWidth,
      offset,
    );
    // ======= First Stacked Bar =======
    let name1 = firstStackedBarInfo.name;
    this.svg[`stacked1Bar${name1}`] = this.createBar(
      "stacked1Bars" + name1,
      data,
      show,
    );
    // balance exposure bars are called stackedBar1
    this.styleBar({
      bar: this.svg[`stacked1Bar${name1}`],
      barInfo: firstStackedBarInfo,
      strokeWidth,
      xField,
      delta,
      yField1,
      className: "stacked1Bar" + name1,
      candleWidth,
      offset: offsets,
      idField,
      addId,
    });

    // ======= Second Stacked Bar =======
    let name2 = secondStackedBarInfo.name;
    this.svg[`stacked2Bar${name2}`] = this.createBar(
      "stacked2Bars" + name2,
      data,
      show,
    );

    this.styleBar({
      bar: this.svg[`stacked2Bar${name2}`],
      barInfo: secondStackedBarInfo,
      strokeWidth,
      xField,
      delta,
      yField1,
      yField2,
      className: "stacked2Bar" + name2,
      candleWidth,
      crispEdges: false,
      offset: offsets,
      idField,
      addId,
    });
  }

  // MULTI STACKED BARS
  multipleStackedBars({
    data,
    barWidth,
    nameBars,
    colors,
    fillColors,
    xField = "date",
    yFields,
    show = true,
    strokeWidth = 1,
    addId = false,
    idField = "id",
  }) {
    const svg = this.svg;

    const bandWidth = svg.x.bandwidth();
    const candleWidth =
      Math.floor(min([bandWidth * barWidth, 450]) / 2) * 2 + 1;
    const delta = Math.round((bandWidth - candleWidth) / 2);

    yFields.forEach((yField, i) => {
      const color = colors[i];
      const fillColor = fillColors[i];

      const stackedBar = this.createBar(
        `stackedBars${nameBars[i]}`,
        data,
        show,
      );
      this.svg[`${nameBars[i]}`] = stackedBar;

      this.styleBar({
        bar: stackedBar,
        strokeWidth,
        xField,
        delta,
        className: `stackedBar${nameBars[i]}`,
        candleWidth,
        multiYFields: { yFields, yField, color, fillColor },
        index: i,
        addId,
        idField,
      });
    });
    return this.svg;
  }

  // GENERAL METHODS
  calculateWidths(barWidth, offsets = 0) {
    let bandWidth = this.svg.x.bandwidth(); // from scale
    let candleWidth = Math.floor(min([bandWidth * barWidth, 450]) / 2) * 2 + 1; // give space between bars
    let delta = Math.round((bandWidth - candleWidth) / 2); // space given
    offsets = offsets === 0 ? 0 : offsets * bandWidth - delta;
    return [bandWidth, candleWidth, delta, offsets];
  }

  createBar(className, data, show = true, isMultipleBars = false) {
    const existing = this.svgAux.select(`.${className}`);
    if (!existing.empty()) {
      existing.remove();
    }

    if (isMultipleBars) {
      const bar = this.svgAux
        .selectAll(`.${className}`)
        .data([data])
        .enter()
        .append("g")
        .attr("class", className)
        .style("opacity", show ? 1 : 0);

      return bar;
    } else {
      const bar = this.svgAux
        .append("g")
        .datum(data)
        .attr("class", className)
        .style("opacity", show ? 1 : 0);

      return bar;
    }
  }

  styleBar({
    bar,
    strokeWidth,
    xField,
    delta,
    className,
    candleWidth,
    crispEdges = true,
    barInfo = {},
    yField1 = "",
    yField2 = "",
    multiYFields = null,
    index = null,
    idField = "id",
    addId = false,
    offset = 0,
  }) {
    bar
      .selectAll("rect")
      .data((d) => d)
      .enter()
      .append("rect")
      .style("fill", () =>
        multiYFields ? multiYFields.fillColor : barInfo.fillColor,
      )
      .style("stroke", () =>
        multiYFields ? multiYFields.color : barInfo.color,
      )
      .style("stroke-width", strokeWidth)
      .attr("x", (d) => this.svg.x(d[xField]) + delta + offset)
      .attr("y", (d) => {
        if (multiYFields) {
          let accumulated = 0;
          const yField = multiYFields.yFields[index];
          const isValueNegative = d[yField] < 0;
          for (let j = 0; j < index; j++) {
            const value = +d[multiYFields.yFields[j]];
            if (isValueNegative && value < 0) {
              accumulated += value;
            } else if (!isValueNegative && value > 0) {
              accumulated += value;
            }
          }
          const baseValue = accumulated;
          const currentValue = +d[yField];
          const yBase = this.svg.y(baseValue);
          const yTop = this.svg.y(baseValue + currentValue);
          return Math.min(yBase, yTop);
        } else if (yField2) {
          return Math.min(
            this.svg.y(d[yField2] + d[yField1]),
            this.svg.y(d[yField1]),
          );
        } else {
          return Math.min(this.svg.y(0), this.svg.y(d[yField1]));
        }
      })
      .attr("class", (d, i) => {
        return `${className}${addId ? +d[idField] : i}`;
      })
      .attr("height", (d) => {
        const yValue = multiYFields
          ? d[multiYFields.yField]
          : yField2
          ? d[yField2]
          : d[yField1];

        const height = Math.abs(this.svg.y(0) - this.svg.y(yValue));
        return height;
      })
      .attr("width", candleWidth)
      .style("shape-rendering", () => {
        return crispEdges ? "crispEdges" : "";
      });
  }

  generalBars({
    data,
    customName,
    yField = "value",
    fieldColor,
    fieldBackground = null,
    lineWidth = 1,
    idField = "id",
    candleWidth,
    toZero = true,
    xField = "date",
    height = 2,
    yLowerField = 0,
    multipleHeight = false,
    xIsDate = true,
    offset = 0,
    containerElement = true,
    selectAllElements = "rect",
    svgX1 = false,
    keys = [],
  }) {
    const _this = this;
    let delta;
    if (!xIsDate) {
      let bandWidth = this.svg.x.bandwidth();
      delta = Math.round((bandWidth - candleWidth) / 2);
      offset = offset === 0 ? 0 : offset * bandWidth - delta;
    }

    let elementContainer;
    if (containerElement) {
      elementContainer = this.svgAux;
    } else {
      elementContainer = this.svg[customName];
    }

    function getGeneralBars(svg, customSvgName) {
      let container;

      if (svgX1) {
        // Add columns to data
        data.unshift({ columns: keys });

        container = elementContainer
          .selectAll("g.axis--x")
          .data(data)
          .enter()
          .append("g")
          .attr("transform", function(d) {
            return "translate(" + _this.svg.x(d[xField]) + ",0)";
          })
          .selectAll(selectAllElements)
          .data((d) => keys.map((key) => ({ key: key, value: d[key] })));
      } else {
        container = elementContainer
          .selectAll(selectAllElements)
          .data(data, (d) => d && d[idField]);
      }

      _this[svg][customSvgName] = container
        .enter()
        .append("rect")
        .attr("x", (d) => {
          const totalOffset = xIsDate ? -candleWidth / 2 : +delta + offset;
          if (svgX1) {
            return _this.svg.x1(d.key);
          } else {
            return _this.svg.x(d[xField]) + totalOffset || 0;
          }
        })
        .attr("y", (d) => {
          if (!Number.isNaN(d[yField])) {
            if (svgX1) {
              return _this.svg.y(d[yField]);
            }
            return toZero
              ? Math.min(_this.svg.y(0), _this.svg.y(d[yField]))
              : _this.svg.y(d[yField]) - (multipleHeight ? 0 : height / 2);
          }
        })
        .attr("class", (d) => {
          return "bar" + customName + "" + +d[idField];
        })
        .attr("height", (d) => {
          if (!Number.isNaN(d[yField])) {
            const yDistance = Math.abs(_this.svg.y(0) - _this.svg.y(d[yField]));
            if (svgX1) {
              return yDistance;
            }
            return toZero
              ? Math.max(1, yDistance)
              : multipleHeight
              ? _this.svg.y(d[yLowerField]) - _this.svg.y(d[yField])
              : height;
          }
        })
        .attr("width", candleWidth)
        .attr("stroke-width", lineWidth)
        .attr("stroke", (d) =>
          svgX1
            ? _this.svg.colorScale(d.key)
            : d[fieldColor]
            ? d[fieldColor]
            : fieldColor,
        )
        .style("fill", (d) =>
          svgX1
            ? _this.svg.colorScale(d.key)
            : d[fieldBackground]
            ? d[fieldBackground]
            : fieldBackground,
        )
        .style("shape-rendering", "crispEdges")
        .style("vector-effect", "non-scaling-stroke");
      return _this.svg;
    }

    if (containerElement) {
      getGeneralBars("svg", customName);
    } else {
      getGeneralBars("svg", "");
    }
    return this.svg;
  }
}
